import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, List, ListItemButton, ListItemText, Stack, CircularProgress, Snackbar, Alert, IconButton, Modal, TextField, Button, MenuItem, InputAdornment, Card, CardContent, Grid } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import api from "../utils/api";
import NavBar from "../components/NavBar";

interface MealIngredient {
	ingredientId: string;
	ingredientName: string;
	rawQuantity: number;
	quantity: number;
	measurementUnit: string;
}

interface Meal {
	Id: string;
	name: string;
	ingredients: MealIngredient[];
}

interface Ingredient {
	Id: string;
	name: string;
	cost?: number;
	costMeasure?: string;
}

const IngredientDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const [ingredient, setIngredient] = useState<Ingredient | null>(null);
	const [meals, setMeals] = useState<Meal[]>([]);
	const [loading, setLoading] = useState(true);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
		open: false,
		message: "",
		severity: "success"
	});
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [editIngredientName, setEditIngredientName] = useState("");
	const [editCost, setEditCost] = useState<string>("");
	const [editPurchaseQty, setEditPurchaseQty] = useState<string>("");
	const [editPurchaseUnit, setEditPurchaseUnit] = useState<string>("grams");

	useEffect(() => {
		const fetchIngredientDetails = async () => {
			try {
				setLoading(true);
				// Fetch ingredient details
				const ingredientResponse = await api.get<Ingredient>(`/ingredients/${id}`);
				if (ingredientResponse.data) {
					setIngredient(ingredientResponse.data);
				} else {
					setIngredient(null);
					setLoading(false);
					return;
				}

				// Fetch meals containing the ingredient
				try {
					const mealsResponse = await api.get<Meal[]>(`/meals/ingredient/${id}`);
					if (mealsResponse.data && mealsResponse.data.length > 0) {
						const mealsWithIngredients = await Promise.all(
							mealsResponse.data.map(async (meal) => {
								const ingredientsResponse = await api.get(`/meals/${meal.Id}/ingredients`);
								return {
									...meal,
									ingredients: ingredientsResponse.data || []
								};
							})
						);
						mealsWithIngredients.sort((a, b) => a.name.localeCompare(b.name));
						setMeals(mealsWithIngredients);
					} else {
						setMeals([]); // No meals, but not an error
					}
				} catch (mealsError: any) {
					console.log("Error fetching meals for ingredient", mealsError);
					// If meals endpoint returns 404, treat as no meals, not an error
					if (mealsError?.response?.status === 404) {
						setMeals([]);
					} else {
						throw mealsError;
					}
				}
			} catch (error) {
				console.error("Failed to fetch ingredient details", error);
				setSnackbar({ open: true, message: "Failed to load ingredient details", severity: "error" });
			} finally {
				setLoading(false);
			}
		};

		fetchIngredientDetails();
	}, [id]);

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	// Calculate the per-unit cost (per kg or per litre) from purchase details
	const calculatePerUnitCost = (): { cost: number; measure: string } | null => {
		const cost = parseFloat(editCost);
		const qty = parseFloat(editPurchaseQty);

		if (isNaN(cost) || isNaN(qty) || qty <= 0 || cost < 0) {
			return null;
		}

		// Convert to base unit (kg or litre)
		if (editPurchaseUnit === "grams") {
			// Convert grams to kg: cost per kg = cost / (qty / 1000)
			return { cost: cost / (qty / 1000), measure: "kg" };
		} else if (editPurchaseUnit === "kg") {
			return { cost: cost / qty, measure: "kg" };
		} else if (editPurchaseUnit === "ml") {
			// Convert ml to litre: cost per litre = cost / (qty / 1000)
			return { cost: cost / (qty / 1000), measure: "litre" };
		} else if (editPurchaseUnit === "litres") {
			return { cost: cost / qty, measure: "litre" };
		} else if (editPurchaseUnit === "each") {
			return { cost: cost / qty, measure: "each" };
		} else if (editPurchaseUnit === "dozen") {
			// Convert dozen to each: cost per each = cost / (qty * 12)
			return { cost: cost / (qty * 12), measure: "each" };
		}

		return null;
	};

	// Get the calculated cost for display
	const calculatedCost = calculatePerUnitCost();

	// Calculate cost conversions for display
	const getCostConversions = (cost: number, unit: string) => {
		const conversions: { label: string; value: string }[] = [];

		// Add primary cost
		conversions.push({
			label: `per ${unit}`,
			value: `$${cost.toFixed(2)}`
		});

		// Add common conversions based on unit
		if (unit === 'kg') {
			conversions.push({
				label: 'per 100g',
				value: `$${(cost / 10).toFixed(2)}`
			});
			conversions.push({
				label: 'per gram',
				value: `$${(cost / 1000).toFixed(3)}`
			});
		} else if (unit === 'litre' || unit === 'litres') {
			conversions.push({
				label: 'per 100ml',
				value: `$${(cost / 10).toFixed(2)}`
			});
			conversions.push({
				label: 'per ml',
				value: `$${(cost / 1000).toFixed(3)}`
			});
		} else if (unit === 'dozen') {
			conversions.push({
				label: 'per unit',
				value: `$${(cost / 12).toFixed(2)}`
			});
		} else if (unit === 'each') {
			conversions.push({
				label: 'per dozen',
				value: `$${(cost * 12).toFixed(2)}`
			});
		}

		return conversions;
	};

	const handleOpenEditModal = () => {
		if (ingredient) {
			setEditIngredientName(ingredient.name);
			// Reset cost fields - user will enter new purchase details
			setEditCost("");
			setEditPurchaseQty("");
			setEditPurchaseUnit("grams");
			setIsEditModalOpen(true);
		}
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditIngredientName("");
		setEditCost("");
		setEditPurchaseQty("");
		setEditPurchaseUnit("grams");
	};

	const handleSubmitEdit = async () => {
		const formattedIngredientName = editIngredientName.trim();
		if (!formattedIngredientName) {
			setSnackbar({
				open: true,
				message: "Please enter an ingredient name",
				severity: "error"
			});
			return;
		}

		const ingredientData: any = { name: formattedIngredientName };

		// If cost fields are filled, calculate and save the per-unit cost
		if (editCost && editPurchaseQty) {
			const calculated = calculatePerUnitCost();
			if (!calculated) {
				setSnackbar({
					open: true,
					message: "Please enter valid cost and quantity",
					severity: "error"
				});
				return;
			}
			ingredientData.cost = calculated.cost;
			ingredientData.costMeasure = calculated.measure;
		}

		try {
			await api.put(`/ingredients/${id}`, ingredientData);
			setSnackbar({ open: true, message: "Ingredient updated successfully", severity: "success" });

			// Refetch ingredient details
			const response = await api.get<Ingredient>(`/ingredients/${id}`);
			if (response.data) {
				setIngredient(response.data);
			}
			handleCloseEditModal();
		} catch (error) {
			console.error("Failed to update ingredient", error);
			setSnackbar({ open: true, message: "Failed to update ingredient", severity: "error" });
		}
	};

	if (loading) {
		return (
			<Box display="flex" alignItems="center" justifyContent="center" height="100vh">
				<CircularProgress />
			</Box>
		);
	}

	if (!ingredient) {
		return (
			<>
				<NavBar />
				<Box p={3}>
					<Typography variant="h5">Ingredient not found</Typography>
				</Box>
			</>
		);
	}

	return (
		<>
			<NavBar />
			<Box p={3}>
				<Box display="flex" alignItems="center" mb={2}>
					<Typography variant="h4" sx={{ flex: 1 }}>
						{ingredient.name}
					</Typography>
					<IconButton
						aria-label="edit"
						onClick={handleOpenEditModal}
						color="primary">
						<EditIcon />
					</IconButton>
				</Box>

				{/* Cost Information Section */}
				{(ingredient.cost !== undefined && ingredient.cost !== null && ingredient.costMeasure) ? (
					<Card sx={{ mb: 3 }}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Cost Information
							</Typography>
							<Grid container spacing={2} sx={{ mt: 1 }}>
								{getCostConversions(ingredient.cost, ingredient.costMeasure).map((conversion, index) => (
									<Grid item xs={12} sm={6} md={4} key={index}>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{conversion.label}
											</Typography>
											<Typography variant="h6">{conversion.value}</Typography>
										</Box>
									</Grid>
								))}
							</Grid>
						</CardContent>
					</Card>
				) : (
					<Card sx={{ mb: 3 }}>
						<CardContent>
							<Typography variant="h6" gutterBottom>
								Cost Information
							</Typography>
							<Alert severity="warning" sx={{ mt: 2 }}>
								<Typography variant="body2">
									No cost information available for this ingredient
								</Typography>
							</Alert>
						</CardContent>
					</Card>
				)}

				<Typography variant="h5" gutterBottom>
					Meals Containing This Ingredient
				</Typography>

				{meals.length > 0 ? (
					<List>
						<Stack direction="row" spacing={2} pb={2}>
							<Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
								Meal Name
							</Typography>
							<Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
								Raw Weight
							</Typography>
							<Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
								Cooked Weight
							</Typography>
						</Stack>
						{meals.map((meal) => {
							const mealIngredient =
								meal.ingredients && meal.ingredients.length > 0
									? meal.ingredients.find((ing) => ing.ingredientId === ingredient?.Id)
									: null;

							const rawWeight =
								mealIngredient && mealIngredient.rawQuantity
									? `${mealIngredient.rawQuantity} ${mealIngredient.measurementUnit}`
									: "N/A";

							const cookedWeight =
								mealIngredient && mealIngredient.quantity ? `${mealIngredient.quantity} ${mealIngredient.measurementUnit}` : "N/A";

							return (
								<ListItemButton
									key={meal.Id}
									component={RouterLink}
									to={`/meals/${meal.Id}`}
									sx={{
										"&:hover": {
											backgroundColor: "#f5f5f5"
										}
									}}>
									<Stack direction="row" spacing={2} width="100%">
										<ListItemText primary={meal.name} sx={{ flex: 1 }} />
										<ListItemText primary={rawWeight} sx={{ flex: 1 }} />
										<ListItemText primary={cookedWeight} sx={{ flex: 1 }} />
									</Stack>
								</ListItemButton>
							);
						})}
					</List>
				) : (
					<Typography variant="body1">No meals use this ingredient.</Typography>
				)}
			</Box>

			{/* Edit Ingredient Modal */}
			<Modal
				open={isEditModalOpen}
				onClose={handleCloseEditModal}
				aria-labelledby="edit-ingredient-modal-title"
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Box sx={{ width: "80vw", maxWidth: 400, bgcolor: "background.paper", p: 4, borderRadius: 2, boxShadow: 24 }}>
					<Typography id="edit-ingredient-modal-title" variant="h6" gutterBottom>
						Edit Ingredient
					</Typography>
					<TextField
						fullWidth
						label="Enter Ingredient Name"
						variant="outlined"
						value={editIngredientName}
						onChange={(e) => setEditIngredientName(e.target.value)}
						sx={{ marginBottom: 2 }}
						required
					/>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
						Cost Information (optional)
					</Typography>
					<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
						<TextField
							label="Price Paid"
							variant="outlined"
							type="number"
							value={editCost}
							onChange={(e) => setEditCost(e.target.value)}
							sx={{ flex: 1 }}
							slotProps={{
								input: {
									startAdornment: <InputAdornment position="start">$</InputAdornment>
								}
							}}
						/>
						<Typography variant="body1" sx={{ alignSelf: "center" }}>
							for
						</Typography>
						<TextField
							label="Qty"
							variant="outlined"
							type="number"
							value={editPurchaseQty}
							onChange={(e) => setEditPurchaseQty(e.target.value)}
							sx={{ width: 100 }}
						/>
						<TextField
							select
							label="Unit"
							variant="outlined"
							value={editPurchaseUnit}
							onChange={(e) => setEditPurchaseUnit(e.target.value)}
							sx={{ width: 100 }}>
							<MenuItem value="grams">g</MenuItem>
							<MenuItem value="kg">kg</MenuItem>
							<MenuItem value="ml">ml</MenuItem>
							<MenuItem value="litres">L</MenuItem>
							<MenuItem value="each">each</MenuItem>
							<MenuItem value="dozen">dozen</MenuItem>
						</TextField>
					</Box>
					{calculatedCost && (
						<Alert severity="info" sx={{ mb: 2 }}>
							= ${calculatedCost.cost.toFixed(2)} per {calculatedCost.measure}
						</Alert>
					)}
					<Button variant="contained" color="primary" onClick={handleSubmitEdit} fullWidth>
						Save
					</Button>
				</Box>
			</Modal>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default IngredientDetailPage;
